import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CornerDownRight, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";

import { AssemblySkuCombobox } from "@/components/mrp/AssemblySkuCombobox";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { SaveChangesButton } from "@/components/ui/SaveChangesButton";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HoverHintContent } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { formSnapshotDirty } from "@/lib/formDirty";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";

const DEFAULTS_QUERY_KEY = ["manufacturing", "cm-po", "work-order-sku-defaults"] as const;

interface DefaultRowPayload {
  id: number;
  purchase_order_sku: string;
  work_order_skus: string[];
}

interface EditableEntry {
  localKey: string;
  purchase_order_sku: string;
  work_order_skus: string[];
}

function nextLocalKey(): string {
  return `row-${crypto.randomUUID()}`;
}

/** One blank work-order SKU slot (blank string drives the AssemblySku input). */
function oneBlankWorkOrderSkuSlot(): string {
  return "";
}

function emptyEditableEntry(): EditableEntry {
  return {
    localKey: nextLocalKey(),
    purchase_order_sku: "",
    work_order_skus: [oneBlankWorkOrderSkuSlot()],
  };
}

function cloneEntry(entry: EditableEntry): EditableEntry {
  return {
    localKey: entry.localKey,
    purchase_order_sku: entry.purchase_order_sku,
    work_order_skus: [...entry.work_order_skus],
  };
}

function payloadToEditable(rows: DefaultRowPayload[]): EditableEntry[] {
  return rows.map((row) => ({
    localKey: `persisted-${row.id}`,
    purchase_order_sku: row.purchase_order_sku.trim(),
    work_order_skus:
      row.work_order_skus.length > 0
        ? row.work_order_skus.map((sku) => sku.trim())
        : [oneBlankWorkOrderSkuSlot()],
  }));
}

/** Stable JSON for comparing the editor to the latest server payload from React Query. */
function fingerprintPresetRows(entries: EditableEntry[]): string {
  const normalized = entries
    .map((entry) => {
      const purchase_order_sku = entry.purchase_order_sku.trim();
      const work_order_skus = entry.work_order_skus.map((sku) => sku.trim()).filter((sku) => sku.length > 0);
      if (purchase_order_sku.length === 0 && work_order_skus.length === 0) {
        return null;
      }
      return { purchase_order_sku, work_order_skus };
    })
    .filter((row): row is { purchase_order_sku: string; work_order_skus: string[] } => row != null)
    .sort((rowA, rowB) =>
      rowA.purchase_order_sku.toLowerCase().localeCompare(rowB.purchase_order_sku.toLowerCase()),
    );
  return JSON.stringify(normalized);
}

function fingerprintServerRows(rows: DefaultRowPayload[]): string {
  const normalized = rows
    .map((row) => ({
      purchase_order_sku: row.purchase_order_sku.trim(),
      work_order_skus: row.work_order_skus.map((sku) => sku.trim()),
    }))
    .sort((rowA, rowB) =>
      rowA.purchase_order_sku.toLowerCase().localeCompare(rowB.purchase_order_sku.toLowerCase()),
    );
  return JSON.stringify(normalized);
}

function nonEmptyWorkOrderSkus(entry: EditableEntry): string[] {
  return entry.work_order_skus.map((sku) => sku.trim()).filter((sku) => sku.length > 0);
}

type MappingModalState =
  | { open: false }
  | { open: true; mode: "add"; initial: EditableEntry }
  | { open: true; mode: "edit"; initial: EditableEntry };

function SkuMappingModal({
  mode,
  initial,
  hubWritesAllowed,
  saveMutationPending,
  onClose,
  onCommit,
}: {
  mode: "add" | "edit";
  initial: EditableEntry;
  hubWritesAllowed: boolean;
  saveMutationPending: boolean;
  onClose: () => void;
  onCommit: (entry: EditableEntry) => void;
}) {
  const [draft, setDraft] = useState(() => cloneEntry(initial));
  const [modalError, setModalError] = useState<string | null>(null);
  const disabled = !hubWritesAllowed || saveMutationPending;
  const mappingDirty = formSnapshotDirty(
    {
      purchase_order_sku: draft.purchase_order_sku.trim(),
      work_order_skus: draft.work_order_skus.map((sku) => sku.trim()),
    },
    {
      purchase_order_sku: initial.purchase_order_sku.trim(),
      work_order_skus: initial.work_order_skus.map((sku) => sku.trim()),
    },
  );

  useEffect(() => {
    setDraft(cloneEntry(initial));
    setModalError(null);
  }, [initial]);

  function validateAndCommit(): void {
    setModalError(null);
    const purchase_order_sku = draft.purchase_order_sku.trim();
    const work_order_skus = nonEmptyWorkOrderSkus(draft);
    if (purchase_order_sku.length === 0 && work_order_skus.length === 0) {
      setModalError("Enter a purchase order SKU and at least one work order SKU.");
      return;
    }
    if (purchase_order_sku.length === 0 && work_order_skus.length > 0) {
      setModalError("Enter the purchase order SKU you buy.");
      return;
    }
    if (work_order_skus.length === 0) {
      setModalError("Add at least one finished-good work order SKU.");
      return;
    }
    onCommit({
      ...draft,
      purchase_order_sku,
      work_order_skus,
    });
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex min-h-0 items-start justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cm-po-sku-mapping-modal-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <Card className="relative z-[101] my-4 w-full max-w-lg shadow-xl">
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <h3 id="cm-po-sku-mapping-modal-title" className="text-base font-semibold text-slate-900">
              {mode === "add" ? "Add mapping" : "Edit mapping"}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-200 bg-white p-1 text-slate-500 hover:text-slate-900"
              aria-label="Close"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Purchase order SKU matches a CM PO line; work order SKUs are the finished goods Megaventory should create for
            that line when presets apply.
          </p>
        </CardHeader>
        <CardBody className="space-y-3 border-t border-slate-100">
          {modalError ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">{modalError}</p>
          ) : null}
          <div className="space-y-1">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Purchase order SKU</span>
            <AssemblySkuCombobox
              enforceCatalogSku
              disabled={disabled}
              value={draft.purchase_order_sku}
              onChange={(next) => setDraft((previous) => ({ ...previous, purchase_order_sku: next }))}
              onBlurCommittedWithSku={(committed) =>
                setDraft((previous) => ({ ...previous, purchase_order_sku: committed }))
              }
              placeholder="SKU you buy…"
            />
          </div>
          <div className="rounded-md bg-slate-50 p-3">
            <div className="mb-2 flex flex-wrap items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-600">
              <span>Work order SKUs (finished-good)</span>
              {hubWritesAllowed ? (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={saveMutationPending}
                  className="h-7 px-2 text-xs"
                  onClick={() => {
                    setDraft((previous) => ({
                      ...previous,
                      work_order_skus: [...previous.work_order_skus, oneBlankWorkOrderSkuSlot()],
                    }));
                  }}
                >
                  Add work order SKU
                </Button>
              ) : null}
            </div>
            <ul className="space-y-2">
              {draft.work_order_skus.map((sku, woIndex) => (
                <li key={`${draft.localKey}-wo-${woIndex}`} className="flex items-center gap-2">
                  <AssemblySkuCombobox
                    enforceCatalogSku
                    className="flex-1"
                    disabled={disabled}
                    value={sku}
                    onChange={(next) =>
                      setDraft((previous) => {
                        const wcopy = [...previous.work_order_skus];
                        if (woIndex >= wcopy.length) return previous;
                        wcopy[woIndex] = next;
                        return { ...previous, work_order_skus: wcopy };
                      })
                    }
                    onBlurCommittedWithSku={(committed) =>
                      setDraft((previous) => {
                        const wcopy = [...previous.work_order_skus];
                        if (woIndex >= wcopy.length) return previous;
                        wcopy[woIndex] = committed;
                        return { ...previous, work_order_skus: wcopy };
                      })
                    }
                    placeholder={`Finished-good SKU slot ${woIndex + 1}…`}
                  />
                  {hubWritesAllowed && draft.work_order_skus.length > 1 ? (
                    <button
                      type="button"
                      aria-label={`Remove work order SKU ${woIndex + 1}`}
                      disabled={saveMutationPending}
                      onClick={() => {
                        setDraft((previous) => {
                          if (previous.work_order_skus.length < 2) return previous;
                          const wcopy = previous.work_order_skus.filter((_, index) => index !== woIndex);
                          return {
                            ...previous,
                            work_order_skus: wcopy.length > 0 ? wcopy : [oneBlankWorkOrderSkuSlot()],
                          };
                        });
                      }}
                      className="inline-flex shrink-0 rounded-md p-1.5 text-slate-400 hover:bg-white hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
          <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
            <Button type="button" size="sm" variant="secondary" disabled={saveMutationPending} onClick={onClose}>
              Cancel
            </Button>
            {mode === "add" ? (
              <Button type="button" size="sm" disabled={disabled || !mappingDirty} onClick={validateAndCommit}>
                Add mapping
              </Button>
            ) : (
              <SaveChangesButton
                type="button"
                size="sm"
                isDirty={mappingDirty}
                disabled={disabled}
                onClick={validateAndCommit}
              >
                Save changes
              </SaveChangesButton>
            )}
          </div>
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}

/**
 * Maintain saved CM PO SKU → work order SKU rows. Values apply when opening Create CM PO and the line still has blank work-order SKUs.
 */
export function CmPoWorkOrderSkuDefaultsPage() {
  const hubWritesAllowed = useManufacturingScopeHubWrite();
  const queryClient = useQueryClient();
  const lastServerFingerprintRef = useRef<string | null>(null);
  const [baselineReady, setBaselineReady] = useState(false);

  const listQuery = useQuery({
    queryKey: DEFAULTS_QUERY_KEY,
    queryFn: () => apiFetch<{ rows: DefaultRowPayload[] }>("/api/manufacturing/cm-po/work-order-sku-defaults"),
  });

  const [entries, setEntries] = useState<EditableEntry[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [mappingModal, setMappingModal] = useState<MappingModalState>({ open: false });

  useEffect(() => {
    if (!listQuery.isSuccess || !listQuery.data) {
      return;
    }
    const serverFp = fingerprintServerRows(listQuery.data.rows);
    if (serverFp === lastServerFingerprintRef.current) {
      return;
    }
    lastServerFingerprintRef.current = serverFp;
    setEntries(listQuery.data.rows.length > 0 ? payloadToEditable(listQuery.data.rows) : []);
    setBaselineReady(true);
  }, [listQuery.isSuccess, listQuery.data]);

  const dirty = useMemo(() => {
    if (!baselineReady || !listQuery.isSuccess || !listQuery.data) {
      return false;
    }
    return fingerprintPresetRows(entries) !== fingerprintServerRows(listQuery.data.rows);
  }, [baselineReady, entries, listQuery.isSuccess, listQuery.data]);

  const saveMutation = useMutation({
    mutationFn: (body: { rows: { purchase_order_sku: string; work_order_skus: string[] }[] }) =>
      apiFetch<{ rows: DefaultRowPayload[] }>("/api/manufacturing/cm-po/work-order-sku-defaults", {
        method: "PUT",
        body,
      }),
    onSuccess: (response) => {
      setSaveError(null);
      queryClient.setQueryData(DEFAULTS_QUERY_KEY, response);
    },
    onError: (err: Error) => {
      const message =
        err instanceof ApiError ? err.message : err.message.length > 0 ? err.message : "Save failed.";
      setSaveError(message);
    },
  });

  async function handleSave() {
    if (!hubWritesAllowed) return;
    setSaveError(null);
    const rows: { purchase_order_sku: string; work_order_skus: string[] }[] = [];
    for (const entry of entries) {
      const purchase_order_sku = entry.purchase_order_sku.trim();
      const work_order_skus = nonEmptyWorkOrderSkus(entry);
      if (purchase_order_sku.length === 0 && work_order_skus.length === 0) {
        continue;
      }
      if (purchase_order_sku.length === 0 && work_order_skus.length > 0) {
        setSaveError("Each row needs a purchase order SKU if work order SKUs are set.");
        return;
      }
      if (work_order_skus.length === 0) {
        setSaveError(`Purchase order SKU ${purchase_order_sku} needs at least one work order SKU.`);
        return;
      }
      rows.push({ purchase_order_sku, work_order_skus });
    }
    await saveMutation.mutateAsync({ rows });
  }

  function openAddModal() {
    setMappingModal({ open: true, mode: "add", initial: emptyEditableEntry() });
  }

  function openEditModal(entry: EditableEntry) {
    setMappingModal({ open: true, mode: "edit", initial: cloneEntry(entry) });
  }

  function closeMappingModal() {
    setMappingModal({ open: false });
  }

  function commitMappingFromModal(mode: "add" | "edit", entry: EditableEntry) {
    setSaveError(null);
    if (mode === "add") {
      setEntries((previous) => [...previous, entry]);
    } else {
      setEntries((previous) => previous.map((row) => (row.localKey === entry.localKey ? entry : row)));
    }
    closeMappingModal();
  }

  function removeRow(localKey: string) {
    setEntries((previous) => previous.filter((row) => row.localKey !== localKey));
  }

  if (listQuery.isError) {
    return (
      <p className="text-center text-sm text-red-600">
        Failed to load defaults. Refresh to try again.
        {listQuery.error instanceof ApiError ? (
          <span className="mt-2 block font-mono text-xs text-red-700">
            HTTP {listQuery.error.status}: {listQuery.error.message}
          </span>
        ) : null}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Work order SKU defaults</h1>
        <p className="mt-1 text-sm text-slate-500">
          When creating a CM purchase order, picking a PO line SKU that matches a row below pre-fills the work-order SKUs
          on that line as long as you have not typed any finished-good SKUs yet.
        </p>
      </header>

      <Card>
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              Mappings{" "}
              <HoverHintContent
                hoverHint="Purchase order SKU is matched case-insensitively when you populate the compose form. Empty partial rows disappear on save."
                cursor="help"
              >
                How matching works
              </HoverHintContent>
            </CardTitle>
            <p className="mt-1 text-xs text-slate-500 lg:max-w-2xl">
              Use one row per purchase-order line SKU you buy from the contract manufacturer. List every finished-good
              work-order SKU Megaventory should create below that PO line whenever the CM PO composer still leaves the
              work-order section empty.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={saveMutation.isPending || !hubWritesAllowed}
              onClick={openAddModal}
            >
              <Plus className="mr-1 h-4 w-4" />
              Add mapping
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={
                saveMutation.isPending ||
                listQuery.isLoading ||
                listQuery.isPending ||
                !hubWritesAllowed ||
                !dirty
              }
              onClick={() => void handleSave()}
            >
              {saveMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Saving…
                </>
              ) : (
                "Save presets"
              )}
            </Button>
          </div>
        </CardHeader>
        <CardBody className="space-y-3">
          {!hubWritesAllowed ? (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
              You have read-only access to Manufacturing presets. Assign read-write Manufacturing access if you should
              change these mappings.
            </p>
          ) : null}
          {saveError ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">{saveError}</p>
          ) : null}
          {listQuery.isLoading || listQuery.isPending ? (
            <p className="py-10 text-center text-sm text-slate-500">
              <Loader2 className="mr-2 inline h-5 w-5 animate-spin text-slate-400" aria-hidden />
              Loading presets…
            </p>
          ) : entries.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-500">
              No defaults yet — add mappings as you standardize repeating CM PO assemblies.
              {hubWritesAllowed ? (
                <span className="mt-4 block">
                  <Button type="button" size="sm" variant="secondary" onClick={openAddModal}>
                    <Plus className="mr-1 h-4 w-4" />
                    Add mapping
                  </Button>
                </span>
              ) : null}
            </p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
              {entries.map((entry) => {
                const woPills = nonEmptyWorkOrderSkus(entry);
                return (
                  <li key={entry.localKey} className="px-4 py-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="font-mono text-sm font-semibold text-slate-900">
                          {entry.purchase_order_sku.trim() !== "" ? entry.purchase_order_sku.trim() : "—"}
                        </div>
                        <div className="flex gap-2 pl-0.5">
                          <CornerDownRight
                            className="mt-0.5 h-4 w-4 shrink-0 text-slate-400"
                            aria-hidden
                          />
                          {woPills.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                              {woPills.map((sku, index) => (
                                <Badge
                                  key={`${entry.localKey}-wo-${index}-${sku}`}
                                  tone="neutral"
                                  className="font-mono font-normal"
                                >
                                  {sku}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-500">No work order SKUs</span>
                          )}
                        </div>
                      </div>
                      {hubWritesAllowed ? (
                        <div className="flex shrink-0 gap-1">
                          <button
                            type="button"
                            aria-label={`Edit mapping for ${entry.purchase_order_sku || "purchase order SKU"}`}
                            disabled={saveMutation.isPending}
                            onClick={() => openEditModal(entry)}
                            className="inline-flex items-center rounded-md border border-transparent p-2 text-slate-500 transition-colors hover:border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                          >
                            <Pencil className="h-4 w-4" aria-hidden />
                          </button>
                          <button
                            type="button"
                            aria-label={`Remove mapping for ${entry.purchase_order_sku || "purchase order SKU"}`}
                            disabled={saveMutation.isPending}
                            onClick={() => removeRow(entry.localKey)}
                            className="inline-flex items-center rounded-md border border-transparent p-2 text-slate-500 transition-colors hover:border-slate-200 hover:bg-red-50 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>

      {mappingModal.open ? (
        <SkuMappingModal
          mode={mappingModal.mode}
          initial={mappingModal.initial}
          hubWritesAllowed={hubWritesAllowed}
          saveMutationPending={saveMutation.isPending}
          onClose={closeMappingModal}
          onCommit={(entry) => commitMappingFromModal(mappingModal.mode, entry)}
        />
      ) : null}
    </div>
  );
}
